import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import type {
  DismantlingCancelBlocker,
  DismantlingCancelPreview,
  DismantlingSessionDetailDto,
} from './types';
import { useCancelDismantlingSession, useDismantlingSessionCancelPreview } from './useDismantling';

interface CancelDismantlingSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: DismantlingSessionDetailDto | null | undefined;
  showTechnicalDetails?: boolean;
  onCancelled?: () => void;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const countOrZero = (value: number | undefined) => value ?? 0;
const impactCount = (
  impactCounts: Record<string, number> | undefined,
  key: string,
  fallback?: number,
) => impactCounts?.[key] ?? fallback ?? 0;
// Voided rows are not attendance, so the warning counts only active ones.
const activeWorkEntryCount = (preview: DismantlingCancelPreview) =>
  preview.active_work_entry_count ??
  impactCount(preview.impact_counts, 'work_entry_count', preview.work_entry_count);

export const CancelDismantlingSessionDialog = ({
  open,
  onOpenChange,
  session,
  showTechnicalDetails = false,
  onCancelled,
}: CancelDismantlingSessionDialogProps) => {
  const { t } = useTranslation('operations');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const preview = useDismantlingSessionCancelPreview(
    session?.dismantling_session_id,
    open && !!session?.dismantling_session_id,
  );
  const cancelSession = useCancelDismantlingSession();

  useEffect(() => {
    if (open) {
      setReason('');
      setReasonError(null);
    }
  }, [open, session?.dismantling_session_id]);

  const blockers = useMemo<DismantlingCancelBlocker[]>(
    () => preview.data?.blockers ?? [],
    [preview.data?.blockers],
  );
  const blocked =
    preview.data?.can_cancel === false ||
    blockers.length > 0 ||
    countOrZero(preview.data?.blocker_count) > 0;
  const canSubmit =
    !cancelSession.isPending &&
    !preview.isLoading &&
    !preview.isFetching &&
    !preview.error &&
    !blocked &&
    !!preview.data &&
    reason.trim().length > 0;

  const submit = async () => {
    const trimmed = reason.trim();
    if (!session || !trimmed) {
      setReasonError(
        t('efs.operations.dismantling.cancel.reason_required', {
          defaultValue: '取消理由を入力してください。',
        }),
      );
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    try {
      await cancelSession.mutateAsync({
        id: session.dismantling_session_id,
        reason: trimmed,
      });
      onCancelled?.();
      onOpenChange(false);
    } catch {
      // React Query exposes the localized API error below.
    }
  };

  return (
    <Dialog open={open} onOpenChange={cancelSession.isPending ? undefined : onOpenChange}>
      <DialogContent className="max-h-[min(90vh,820px)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.dismantling.cancel.title', {
              defaultValue: '解体を取り消して仕分けへ戻す',
            })}
          </DialogTitle>
          <DialogDescription>
            {session
              ? t('efs.operations.dismantling.cancel.description', {
                  code: session.packages.map((p) => p.package_code).join(', '),
                  defaultValue:
                    '{{code}} の解体記録を取り消し、産出物・中間品・在庫異動を外して仕分けへ戻します。',
                })
              : null}
          </DialogDescription>
        </DialogHeader>

        {session ? (
          <div className="space-y-1 rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-sm">
            <div className="font-mono text-sm font-semibold">
              {session.packages.map((pkg) => pkg.package_code).join(' / ')}
            </div>
            <div className="text-xs text-efs-text-muted">
              {session.dismantling_date} · {session.status}
            </div>
            {showTechnicalDetails ? (
              <div className="text-[11px] text-efs-text-muted">
                {t('efs.operations.dismantling.cancel.technical_session_id', {
                  id: session.dismantling_session_id,
                  defaultValue: 'Session ID: {{id}}',
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          {preview.isLoading || preview.isFetching ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('efs.operations.dismantling.cancel.loading_preview', {
                defaultValue: '影響範囲を確認しています…',
              })}
            </span>
          ) : preview.data ? (
            <div className="space-y-3">
              <p className="font-medium">
                {t('efs.operations.dismantling.cancel.immediate_effect', {
                  defaultValue:
                    '取消後、元パレットは仕分け済み・解体待ちへ戻り、解体の産出物と在庫異動は通常一覧から外れます。',
                })}
              </p>
              <p>
                {t('efs.operations.dismantling.cancel.retention_notice', {
                  purgeAfter: formatDateTime(preview.data.purge_after),
                  defaultValue: '復元期限: {{purgeAfter}}',
                })}
              </p>
              {/* Real attendance days are removed with an unfinished session. */}
              {preview.data.status === 'imported' && activeWorkEntryCount(preview.data) > 0 ? (
                <p
                  role="note"
                  className="rounded-md border border-red-400 bg-red-50 p-2 font-medium text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
                >
                  <AlertTriangle className="mr-1 inline h-4 w-4 align-text-bottom" aria-hidden />
                  {t('efs.operations.dismantling.cancel.work_entries_warning', {
                    count: activeWorkEntryCount(preview.data),
                    defaultValue:
                      'この解体作業には作業記録（出勤記録）が{{count}}件あります。取り消すと一緒に削除されます（30日以内は「最近取消した解体」から復元できます）。実際の作業記録の場合は、確認してから取り消してください。',
                  })}
                </p>
              ) : null}
              <dl className="grid grid-cols-2 gap-2 text-xs">
                {[
                  [
                    'input_packages_label',
                    impactCount(
                      preview.data.impact_counts,
                      'input_package_count',
                      preview.data.input_package_count,
                    ),
                  ],
                  [
                    'work_entries_label',
                    impactCount(
                      preview.data.impact_counts,
                      'work_entry_count',
                      preview.data.work_entry_count,
                    ),
                  ],
                  [
                    'final_outputs_label',
                    impactCount(
                      preview.data.impact_counts,
                      'dismantling_output_count',
                      preview.data.dismantling_output_count ?? preview.data.final_output_count,
                    ),
                  ],
                  [
                    'intermediates_label',
                    impactCount(
                      preview.data.impact_counts,
                      'intermediate_count',
                      preview.data.intermediate_count,
                    ),
                  ],
                  [
                    'derived_packages_label',
                    impactCount(
                      preview.data.impact_counts,
                      'derived_inbound_package_count',
                      preview.data.derived_inbound_package_count ??
                        preview.data.derived_package_count,
                    ),
                  ],
                  [
                    'movements_label',
                    impactCount(
                      preview.data.impact_counts,
                      'inventory_movement_count',
                      preview.data.inventory_movement_count,
                    ),
                  ],
                ].map(([key, count]) => (
                  <div key={key}>
                    <dt className="text-efs-text-muted">
                      {t(`efs.operations.dismantling.cancel.${key}`, {
                        defaultValue: String(key),
                      })}
                    </dt>
                    <dd className="font-semibold">
                      {t('efs.operations.dismantling.cancel.count_unit', {
                        count: count as number,
                        defaultValue: '{{count}} 件',
                      })}
                    </dd>
                  </div>
                ))}
              </dl>
              {blocked ? (
                <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-destructive">
                  <p className="inline-flex items-center gap-1 font-medium">
                    <AlertTriangle className="h-4 w-4" aria-hidden />
                    {t('efs.operations.dismantling.cancel.blocked', {
                      defaultValue: '後続データがあるため取消できません。',
                    })}
                  </p>
                  {blockers.length > 0 ? (
                    <ul className="mt-1 list-disc pl-5">
                      {blockers.map((blocker) => (
                        <li key={blocker.code}>
                          {t(`efs.operations.dismantling.cancel.blockers.${blocker.code}`, {
                            count: blocker.count,
                            defaultValue: t(
                              'efs.operations.dismantling.cancel.blockers.unknown_dependency',
                              {
                                count: blocker.count,
                                defaultValue:
                                  '{{count}} 件の関連データがあります。管理者に確認してください。',
                              },
                            ),
                          })}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : preview.error ? (
            <LocalizedApiError error={preview.error} showTechnicalDetails={showTechnicalDetails} />
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="dismantling-cancel-reason">
            {t('efs.operations.dismantling.cancel.reason', {
              defaultValue: '取消理由',
            })}
            <span className="ml-1 text-red-900">*</span>
          </Label>
          <Textarea
            id="dismantling-cancel-reason"
            ref={reasonRef}
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError && e.target.value.trim()) setReasonError(null);
            }}
            placeholder={t('efs.operations.dismantling.cancel.reason_placeholder', {
              defaultValue: '例: 品目を誤って登録したため仕分けからやり直す',
            })}
          />
          {reasonError ? <p className="text-sm text-destructive">{reasonError}</p> : null}
          {cancelSession.error ? (
            <LocalizedApiError
              error={cancelSession.error}
              showTechnicalDetails={showTechnicalDetails}
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={cancelSession.isPending}
          >
            {t('efs.common.actions.cancel', { ns: 'common' })}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {cancelSession.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="mr-1 h-4 w-4" />
            )}
            {t('efs.operations.dismantling.cancel.confirm', {
              defaultValue: '取消して仕分けへ戻す',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelDismantlingSessionDialog;
